import React from 'react';
import { Box, Flex, Spacer, Text, useToken } from '@chakra-ui/react';
import { Card, ProgressBar } from '..';
import { Tag } from '../tag';
import { HexagonBadge } from '../../icons/hexagon-badge';
import { backgroundToTokens } from '../..';
const Header = (props)=>{
    const { children , ...flexProps } = props;
    return /*#__PURE__*/ React.createElement(Flex, {
        direction: "column",
        pos: "relative",
        w: "full",
        ...flexProps
    }, children);
};
const Media = (props)=>{
    const { children , ...boxProps } = props;
    return /*#__PURE__*/ React.createElement(Box, {
        borderTopRadius: "16px",
        h: "10rem",
        mt: -2,
        overflow: "hidden",
        w: "full",
        ...boxProps
    }, children);
};
const useHexagonColor = (backgroundColor)=>{
    // Fallback to any background initially to avoid a conditional hook
    const { bg  } = backgroundToTokens(backgroundColor ?? 'dark');
    const colorToken = useToken('colors', bg);
    // Now that we've called the hook we can early return
    if (!backgroundColor || !colorToken) return null;
    const isGradient = !colorToken.startsWith('#');
    // Use the URL that refers to SvgGradients if it's a gradient name
    const hexagonColor = isGradient ? `url(#${backgroundColor})` : colorToken; // Otherwise use the color token from color.ts
    return hexagonColor;
};
const Badges = (props)=>{
    const { leftHexagon , primaryTag , rightHexagon , secondaryTag , ...flexProps } = props;
    const leftHexColor = useHexagonColor(leftHexagon);
    const rightHexColor = useHexagonColor(rightHexagon);
    return /*#__PURE__*/ React.createElement(Flex, {
        justify: "space-between",
        px: 2,
        w: "full",
        ...flexProps
    }, (primaryTag || leftHexColor) && /*#__PURE__*/ React.createElement(Flex, {
        gap: 1
    }, leftHexColor && /*#__PURE__*/ React.createElement(HexagonBadge, {
        color: leftHexColor,
        fill: leftHexColor,
        size: "24px"
    }), primaryTag && /*#__PURE__*/ React.createElement(Tag, {
        colorScheme: "pink",
        size: "s",
        ...primaryTag
    })), /*#__PURE__*/ React.createElement(Spacer, null), (secondaryTag || rightHexColor) && /*#__PURE__*/ React.createElement(Flex, {
        gap: 1
    }, secondaryTag && /*#__PURE__*/ React.createElement(Tag, {
        colorScheme: "white",
        size: "s",
        ...secondaryTag
    }), rightHexColor && /*#__PURE__*/ React.createElement(HexagonBadge, {
        color: rightHexColor,
        fill: rightHexColor,
        size: "24px"
    })));
};
const MediaBadges = (props)=>/*#__PURE__*/ React.createElement(Badges, {
        pos: "absolute",
        ...props
    });
const Body = (props)=>{
    const { children , ...flexProps } = props;
    return /*#__PURE__*/ React.createElement(Flex, {
        color: "text.primary",
        direction: "column",
        gap: 1,
        px: 2,
        ...flexProps
    }, children);
};
const Progress = (props)=>/*#__PURE__*/ React.createElement(ProgressBar, {
        ...props,
        position: "relative"
    });
const Title = (props)=>{
    const { children , ...textProps } = props;
    return /*#__PURE__*/ React.createElement(Text, {
        noOfLines: 2,
        textStyle: "2xl",
        ...textProps
    }, children);
};
const Subtitle = (props)=>{
    const { children , ...textProps } = props;
    return /*#__PURE__*/ React.createElement(Text, {
        textStyle: "xlBold",
        ...textProps
    }, children);
};
const Note = (props)=>{
    const { children , ...textProps } = props;
    return /*#__PURE__*/ React.createElement(Text, {
        color: "text.secondary",
        textStyle: "s",
        ...textProps
    }, children);
};
const Tags = (props)=>{
    const { tags  } = props;
    return /*#__PURE__*/ React.createElement(Flex, {
        gap: 1
    }, tags.map((tag)=>/*#__PURE__*/ React.createElement(Tag, {
            colorScheme: "light",
            key: tag,
            size: "s",
            text: tag
        })));
};
const Content = (props)=>{
    const { children , ...flexProps } = props;
    return /*#__PURE__*/ React.createElement(Flex, {
        direction: "column",
        ...flexProps
    }, children);
};
const Actions = (props)=>{
    const { children , ...flexProps } = props;
    return /*#__PURE__*/ React.createElement(Flex, {
        gap: 1,
        justify: "center",
        px: 2,
        ...flexProps
    }, children);
};
export const SuperCard = (props)=>{
    const { children , ...cardProps } = props;
    return /*#__PURE__*/ React.createElement(Card, {
        ...cardProps,
        position: "relative",
        py: 2
    }, React.Children.map(children, (child, index)=>{
        const marginBottom = index === React.Children.count(children) - 1 ? 0 : 2;
        if (/*#__PURE__*/ React.isValidElement(child)) {
            return /*#__PURE__*/ React.cloneElement(child, {
                mb: marginBottom
            });
        }
    }));
};
SuperCard.Header = Header;
SuperCard.Media = Media;
SuperCard.MediaBadges = MediaBadges;
SuperCard.Badges = Badges;
SuperCard.Body = Body;
SuperCard.Progress = Progress;
SuperCard.Title = Title;
SuperCard.Subtitle = Subtitle;
SuperCard.Note = Note;
SuperCard.Tags = Tags;
SuperCard.Content = Content;
SuperCard.Actions = Actions;

//# sourceMappingURL=supercard.js.map